import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Repeat, Plus, Edit2, Trash2, Calendar, Play, Pause } from "lucide-react";

const FREQ_LABEL = { daily: "يومي", weekly: "أسبوعي", monthly: "شهري", quarterly: "ربع سنوي", yearly: "سنوي" };

export default function RecurringInvoices() {
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState({ sale: [], purchase: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", kind: "sale", party_id: "", frequency: "monthly",
    day_of_month: 1, weekday: 0, start_date: new Date().toISOString().slice(0, 10),
    end_date: "", active: true, template_lines: [],
    template_discount_percent: 0, template_notes: "", currency: "SYP",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, cs, sp, prods] = await Promise.all([
        api.get("/recurring-invoices"),
        api.get("/customers"),
        api.get("/suppliers"),
        api.get("/products"),
      ]);
      setItems(r.data);
      setParties({ sale: cs.data, purchase: sp.data });
      setProducts(prods.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "", kind: "sale", party_id: "", frequency: "monthly",
      day_of_month: 1, weekday: 0, start_date: new Date().toISOString().slice(0, 10),
      end_date: "", active: true, template_lines: [],
      template_discount_percent: 0, template_notes: "", currency: "SYP",
    });
    setDlg(true);
  };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, end_date: r.end_date || "" }); setDlg(true); };

  const addLine = () => setForm(f => ({ ...f, template_lines: [...f.template_lines, { product_id: "", product_name: "", quantity: 1, price: 0, unit_name: "قطعة", unit_factor: 1, discount: 0, total: 0 }] }));
  const updLine = (i, patch) => {
    setForm(f => ({
      ...f, template_lines: f.template_lines.map((l, idx) => {
        if (idx !== i) return l;
        const merged = { ...l, ...patch };
        merged.total = (merged.quantity || 0) * (merged.price || 0) - (merged.discount || 0);
        return merged;
      })
    }));
  };
  const rmLine = (i) => setForm(f => ({ ...f, template_lines: f.template_lines.filter((_, idx) => idx !== i) }));

  const pickProduct = (i, pid) => {
    const p = products.find(x => x.id === pid);
    if (p) updLine(i, { product_id: pid, product_name: p.name, price: p.sale_price || 0 });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    if (!form.party_id) return toast.error("اختر عميل/مورد");
    if (form.template_lines.length === 0) return toast.error("أضف صنف واحد على الأقل");
    try {
      const body = { ...form, end_date: form.end_date || null };
      if (editing) await api.put(`/recurring-invoices/${editing.id}`, body);
      else await api.post("/recurring-invoices", body);
      toast.success(editing ? "تم التحديث" : "تم إنشاء الفاتورة المتكررة");
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`حذف "${r.name}"؟`)) return;
    try { await api.delete(`/recurring-invoices/${r.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const toggleActive = async (r) => {
    try { await api.put(`/recurring-invoices/${r.id}`, { ...r, active: !r.active }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const total = form.template_lines.reduce((s, l) => s + (l.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="recurring-invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Repeat className="w-6 h-6 text-primary" /> الفواتير المتكررة</h1>
          <p className="text-sm text-muted-foreground mt-1">أنشئ فواتير تُولَّد تلقائياً حسب جدولة (شهري/أسبوعي/سنوي...) — مثالية للاشتراكات والعقود الدورية</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-recurring-btn"><Plus className="w-4 h-4" /> فاتورة متكررة جديدة</Button>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Repeat className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد فواتير متكررة</div>
          <Button variant="link" onClick={openNew} className="mt-2">أنشئ الأولى</Button>
        </div>
       ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(r => (
            <div key={r.id} className={`border-2 rounded-lg p-4 transition ${r.active ? "border-primary/40 bg-card" : "border-slate-200 bg-slate-50 opacity-70"}`} data-testid={`recurring-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded grid place-items-center ${r.kind === "sale" ? "bg-emerald-100" : "bg-amber-100"}`}>
                    <Repeat className={`w-5 h-5 ${r.kind === "sale" ? "text-emerald-700" : "text-amber-700"}`} />
                  </div>
                  <div>
                    <div className="font-bold text-sm">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.kind === "sale" ? "مبيعات" : "مشتريات"} · {FREQ_LABEL[r.frequency]}</div>
                  </div>
                </div>
                <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} />
              </div>
              <div className="text-xs space-y-1 mt-2 mb-2">
                <div>👤 {r.party_name}</div>
                <div className="flex items-center gap-1 text-muted-foreground"><Calendar className="w-3 h-3" /> التالي: <b className="text-foreground font-mono">{r.next_run_at || "—"}</b></div>
                <div className="text-muted-foreground">أُنشِئ {r.runs_count || 0} مرة</div>
              </div>
              <div className="flex gap-1 border-t pt-2">
                <Button size="sm" variant="ghost" className="h-7 text-xs flex-1" onClick={() => openEdit(r)}><Edit2 className="w-3 h-3 me-1" /> تعديل</Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
       )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="recurring-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل" : "فاتورة متكررة جديدة"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>الاسم</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="اشتراك شهري - عيادة الحياة" data-testid="recurring-name" /></div>
              <div>
                <Label>النوع</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, party_id: "" })}>
                  <SelectTrigger data-testid="recurring-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="sale">فاتورة مبيعات</SelectItem><SelectItem value="purchase">فاتورة مشتريات</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{form.kind === "sale" ? "العميل" : "المورد"}</Label>
                <select className="w-full h-10 px-2 border rounded bg-background" value={form.party_id} onChange={(e) => setForm({ ...form, party_id: e.target.value })} data-testid="recurring-party">
                  <option value="">— اختر —</option>
                  {(parties[form.kind] || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>التكرار</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(FREQ_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(form.frequency === "monthly" || form.frequency === "quarterly" || form.frequency === "yearly") && (
                <div><Label>يوم الشهر</Label><Input type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: +e.target.value || 1 })} /></div>
              )}
              <div><Label>يبدأ من</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="recurring-start" /></div>
              <div><Label>ينتهي في (اختياري)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div><Label>العملة</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="SYP">SYP</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="TRY">TRY</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>خصم %</Label><Input type="number" value={form.template_discount_percent} onChange={(e) => setForm({ ...form, template_discount_percent: +e.target.value || 0 })} /></div>
            </div>

            <div className="border rounded-lg p-3 bg-muted/20">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-sm">قالب الأصناف</div>
                <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 me-1" /> صنف</Button>
              </div>
              {form.template_lines.length === 0 ? <p className="text-xs text-center text-muted-foreground py-2">لم تُضف أصناف بعد</p> :
                form.template_lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 py-2 border-b last:border-0 items-end">
                    <div className="col-span-5">
                      <select className="w-full h-9 px-2 border rounded bg-background text-sm" value={l.product_id} onChange={(e) => pickProduct(i, e.target.value)}>
                        <option value="">— اختر —</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2"><Input type="number" value={l.quantity} onChange={(e) => updLine(i, { quantity: +e.target.value || 0 })} className="h-9" placeholder="كمية" /></div>
                    <div className="col-span-2"><Input type="number" value={l.price} onChange={(e) => updLine(i, { price: +e.target.value || 0 })} className="h-9" placeholder="سعر" /></div>
                    <div className="col-span-2 text-end font-mono text-sm py-2">{(l.total || 0).toLocaleString("ar-EG")}</div>
                    <div className="col-span-1"><Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => rmLine(i)}><Trash2 className="w-3.5 h-3.5" /></Button></div>
                  </div>
                ))
              }
              {form.template_lines.length > 0 && (
                <div className="text-end pt-2 border-t mt-2 font-bold">الإجمالي: <span className="text-primary font-mono">{total.toLocaleString("ar-EG")} {form.currency}</span></div>
              )}
            </div>

            <div><Label>ملاحظات القالب</Label><Textarea rows={2} value={form.template_notes} onChange={(e) => setForm({ ...form, template_notes: e.target.value })} placeholder="نص يُضاف تلقائياً لكل فاتورة مُولَّدة" /></div>
            <div className="flex items-center justify-between p-3 rounded border bg-muted/30">
              <Label>الجدولة نشطة</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-recurring-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
